import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { getConversationPrompt } from 'actions/requests';
import { useThunkDispatch } from 'utils/reduxHook';
import InAppRoutes from 'utils/router';
import { useIsMobile } from 'utils/hooks';
import Typography from 'components/atoms/Typography';
import BackButton from 'components/molecules/BackButton';
import ConversationPromptForm from 'components/molecules/ConversationPromptForm';
import PreviewYourPrompt from 'components/molecules/PreviewYourPrompt';
import SkeletonWrap from 'components/organisms/SkeletonWrap';

function ConversationPromptCreateEdit() {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    message: '',
    promptTip: '',
    videoUrl: '',
    authorImageUrl: '',
    author: '',
    topicsArr: [],
    messageMedia: [],
    status: undefined,
  });
  const dispatch = useThunkDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const conversationPromptId = useParams<{ conversationPromptId: string }>().conversationPromptId;
  const location = useLocation();
  const history = useHistory();
  const isEditing = location.pathname.includes('edit');
  const isMobile = useIsMobile();

  const loadConversationPrompt = async () => {
    try {
      setIsLoading(true);
      if (conversationPromptId) {
        const promptFromParam = await dispatch(getConversationPrompt(conversationPromptId));
        setFormData({
          ...formData,
          id: promptFromParam.id || '',
          name: promptFromParam.name || '',
          message: promptFromParam.message || '',
          promptTip: promptFromParam.promptTip || '',
          videoUrl: promptFromParam.videoUrl || '',
          authorImageUrl: promptFromParam.authorImageUrl || '',
          author: promptFromParam.author || '',
          topicsArr: promptFromParam.topicsArr || [],
          status: promptFromParam.status,
          messageMedia: promptFromParam.messageMedia || [],
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleFormPostSubmit = () => {
    history.replace('/admin/conversation-prompts');
  };

  useEffect(() => {
    loadConversationPrompt();
  }, []);

  return (
    <>
      <div className="flex-3">
        <div className="p-6 h-full">
          <div className="p-6">
            <BackButton
              route={InAppRoutes.AdminConversationPrompts}
              text="Back to All Prompts"
              dataDurableId="backToAllPromptsButton"
            />
            <Typography variant="h3">{isEditing ? 'Edit' : 'Create'} Conversation Prompt</Typography>
            <SkeletonWrap
              isLoading={isLoading}
              skeleton={
                <div className="flex-3">
                  <Skeleton height={60} className="my-2" />
                  <Skeleton height={60} className="my-2" />
                  <Skeleton height={60} className="my-2" />
                  <Skeleton height={60} className="my-2" />
                  <Skeleton height={60} className="my-2" />
                  <Skeleton height={60} className="my-2" />
                  <Skeleton height={60} className="my-2" />
                  <Skeleton height={60} className="my-2" />
                  <Skeleton height={60} className="my-2" />
                </div>
              }
              content={
                <ConversationPromptForm
                  handleFormChange={(field, value) => handleFormChange(field, value)}
                  formData={formData}
                  isEditing={isEditing}
                  setFormData={setFormData}
                  isLoading={isLoading}
                  handleFormPostSubmit={() => handleFormPostSubmit()}
                />
              }
            />
          </div>
        </div>
      </div>
      {!isMobile && <PreviewYourPrompt isLoading={isLoading} prompt={formData} />}
    </>
  );
}

export default ConversationPromptCreateEdit;
